import React, { FC, useContext, useEffect, useMemo, useState } from "react";
import TagCell from "./TagCell";
import { useNavigate } from "react-router";
import { AppContext } from "Context/AppContext";
import { Link } from "react-router";
import { ITEMS, LISTS, LIST_NAME, LIST_READ, ListsEditHeaders } from "./constants";
import { DragDropContext, Droppable, Draggable, DropResult } from "@hello-pangea/dnd";
import useProductAttributeImport from "../ISR/Pages/DefineModel/Hooks/useProductAttributeImport";
import { toast } from "Components/DesignSystem/Toast/useToast";
import useTable from "Components/Common/Table/useTable";
import Typography from "Components/DesignSystem/Typography/Typography";
import Divider from "Components/DesignSystem/Divider/Divider";
import IconButton from "Components/DesignSystem/IconButton/IconButton";
import TextField from "Components/DesignSystem/TextField/TextField";
import Tag from "Components/DesignSystem/Tag/Tag";
import FieldTitle from "Components/DesignSystem/FieldTitle/FieldTitle";
import Card from "Components/DesignSystem/Card/Card";
import Toggle from "Components/DesignSystem/Toggle/Toggle";
import Tooltip from "Components/DesignSystem/Tooltip/Tooltip";
import Button from "Components/DesignSystem/Button/Button";
import PageSkeleton from "Components/DesignSystem/PageSkeleton/PageSkeleton";
import { Sort, ArrowUp, ArrowDown, InfoCircle, ChevronLeft, Add, Drag_n_drop, ImageIcon } from "Components/DesignSystem/Iconography/Icons";
import { HelpTextData } from "../ISR/Pages/DefineModel/model";
import { exportList, getListItem } from "./Services/ListServices";
import { Actions } from "./Actions";

interface Row {
    title: string;
    display_value: string;
    visibility: boolean;
    image: string;
    id?: string;
    math_value: number | null;
    tag?: string;
    helpText?: HelpTextData;
}

interface ListObj {
    id?: string;
    name: string;
    industry_id: string;
    items: Row[];
    imported_attribute: string;
    created_at: string;
    created_by: string;
    updated_at?: string;
    updated_by?: string;
}

const ListsDetails: FC = (props: any) => {
    let appContext = useContext(AppContext);
    const { params } = props.match;
    const { id } = params;
    const [loading, setLoading] = useState(false);
    const appData = useContext(AppContext);
    const navigate = useNavigate();
    const [tableData, setTableData] = useState<Row[]>();
    const [models, setModels] = useState<string[]>([]);
    const [listName, setListName] = useState<string>();
    const [editState, setEditState] = useState<ListObj>();
    const { fieldDataConfiguration } =
        useProductAttributeImport({ attributeId: editState?.imported_attribute as string });

    const { headers, sort, handleSortByColumn } =
        useTable({ table_headers: ListsEditHeaders });

    useEffect(() => {
        const { column, direction } = sort;
        if (!column || !direction) return;
        setTableData(prev => {
            if (!prev) return prev;
            return [...prev].sort((a, b) => {
                const valA = (a as any)[column] ?? "";
                const valB = (b as any)[column] ?? "";
                const numA = Number(valA);
                const numB = Number(valB);
                if (!isNaN(numA) && !isNaN(numB) && valA !== "" && valB !== "") {
                    return direction === "asc" ? numA - numB : numB - numA;
                }
                const strA = valA.toString().toLowerCase();
                const strB = valB.toString().toLowerCase();
                if (strA < strB) return direction === "asc" ? -1 : 1;
                if (strA > strB) return direction === "asc" ? 1 : -1;
                return 0;
            });
        });
    }, [sort]);

    const [lastTableRowRef, setLastTableRowRef] = useState<any>();
    const [downloadUrl, setDownloadUrl] = useState<string | undefined>(undefined);
    const readPermission = appContext.pm.checkPermission(LIST_READ);

    const allTags = useMemo(
        () => Array.from(new Set((tableData ?? []).map(r => r.tag).filter((t): t is string => Boolean(t)))),
        [tableData]
    );

    const doExportList = async () => {
        try {
            if (!editState?.id) return;
            const res = await exportList(editState?.id);
            if (res?.done) {
                setDownloadUrl(res?.data?.url);
                toast.success("Downloaded successfully");
            } else throw new Error("Download Failed");
        } finally {
            setTimeout(() => setDownloadUrl(undefined), 4000);
        }
    };

    useEffect(() => {
        if (lastTableRowRef) {
            lastTableRowRef?.scrollIntoView({ behavior: 'smooth', block: 'end' })
        }
    }, [lastTableRowRef]);


    useEffect(() => {
        if (!readPermission) {
            navigate("/workspace/module/permission/denied");
        }
    }, [appData.channelKey]);

    useEffect(() => {
        const table: Row[] = fieldDataConfiguration.choices?.map((choice) => ({
            title: choice.av_value,
            display_value: choice.av_value,
            visibility: true,
            image: "",
            math_value: null
        })) || [];
        setTableData((prev: Row[] | undefined) => {
            if (!prev) return table;
            const newTable = table.filter(x => {
                if (!prev?.find(y => y.title === x.title)) return x
            })
            return [...prev, ...newTable];
        });
    }, [fieldDataConfiguration]);

    useEffect(() => {
        const fetchEditState = async () => {
            try {
                setLoading(true);
                const res = await getListItem(id);
                if (res?.data?.done) {
                    setListName(res?.data?.data?.name);
                    setTableData(res?.data?.data?.items);
                    setModels(res?.data?.data?.models);
                    setEditState(res?.data?.data);
                }
            } catch (error) {
                console.log(error);
            } finally {
                setLoading(false);
            }
        };
        if (
            ![null, "null", "undefined", undefined].includes(id) &&
            !editState
        ) {
            fetchEditState();
        }
    }, []);

    const onDragEnd = (result: DropResult) => {
        if (!result.destination) return;
        const items = Array.from(tableData ?? []);
        const [reorderedItem] = items.splice(result.source.index, 1);
        items.splice(result.destination.index, 0, reorderedItem);

        setTableData(items);
    };

    const onVisibilityChangeHandler = (index: number) => {
        setTableData((prev) => {
            const newValue = prev?.map((entry, id) => {
                if (id === index) entry.visibility = !entry?.visibility;
                return entry;
            });
            return newValue;
        });
    };

    return (
        <React.Fragment>
            <div className={`tw-px-[21px] tw-pt-[12px] tw-flex tw-items-center tw-gap-3`}>
                {/* DS ChevronLeft on role ink — LeftVectorIcon hardcoded stroke #172B4D,
                    invisible on the dark ground (the MainPageHeader fix, same bug). */}
                <Link
                    to={"/gms/lists"}
                    aria-label="Back to lists"
                    className="tw-flex tw-items-center tw-text-ink-secondary hover:tw-text-ink"
                >
                    <ChevronLeft size={20} />
                </Link>
                {/* Was a <label>: the page had no h1 at all — § 81's header grammar.
                    tw-text-ink is explicit: heading-2 is a size, not an ink, and the
                    inherited default is black — invisible in dark. */}
                <Typography variant="display" as="h1" className="tw-text-ink" data-testid="list-details-page-title">
                    {LISTS}
                </Typography>
            </div>
            <Divider className="tw-mt-2" data-testid="list-details-header-divider" />
            {loading ? (
                <PageSkeleton
                    preset="list"
                    label="Loading list"
                    data-testid="list-details-skeleton"
                    className="tw-px-[21px] tw-mt-3"
                />
            ) : (
                <>
                    <div className="tw-flex tw-mt-2 tw-mb-2">
                        <div className="tw-w-[30%] tw-ms-4">
                            <TextField
                                label={LIST_NAME}
                                required
                                disabled
                                placeholder="Enter Name"
                                value={listName ?? ""}
                                onChange={(value) => setListName(value)}
                                data-testid="list-listdetails-input-1"
                                className="tw-max-w-[364px]"
                            />
                        </div>
                        <div className="tw-w-[70%] tw-ml-16">
                            <FieldTitle label="Associated Models" />
                            <div className="tw-mt-2 tw-flex tw-flex-wrap tw-gap-1" data-testid="list-listdetails-models">
                                {models?.length > 0 ? models?.map((model, index) =>
                                    // Models are plain strings with no ids; index is the only stable key.
                                    // eslint-disable-next-line react/no-array-index-key
                                    <Tag key={index} label={model} variant="meta" data-testid={`list-listdetails-model-${index}`} />
                                ) : (
                                    <Typography variant="body-primary" as="span" className="tw-text-ink-secondary">
                                        No Models Associated
                                    </Typography>
                                )}
                            </div>
                        </div>
                    </div>
                    <div
                        ref={(el) => {
                            setLastTableRowRef(el)
                        }}
                        className="tw-ml-4 tw-w-[97%]"
                    >
                    <Card data-testid="list-listdetails-items-panel">
                        <div className="tw-flex tw-justify-between tw-items-center tw-mb-3">
                            <Typography className="tw-text-ink" variant="title-header" as="h3">
                                {ITEMS}
                            </Typography>
                            <Actions
                                onOpenImportModal={() => { }}
                                onOpenValidateModal={() => { }}
                                onExport={doExportList}
                                disabled={false}
                                openAttributeMenu={() => { }}
                                isReadOnly={true}
                            />
                        </div>
                        {downloadUrl && (
                            <iframe
                                style={{ display: "none" }}
                                src={downloadUrl}
                                id="iframe_list_details_download"
                                title="list_details_download"
                            />
                        )}
                        <DragDropContext onDragEnd={onDragEnd} >
                            <Droppable droppableId="rows">
                                {(provided) => (
                                    <div className="tw-max-h-[510px] tw-overflow-x-auto ">
                                        <table
                                            {...provided.droppableProps}
                                            ref={provided.innerRef}
                                            className=" tw-table-auto tw-w-full tw-overflow-scroll"
                                        >
                                            <thead className=" tw-sticky tw-top-0 tw-bg-surface-sunken  tw-border-edge tw-h-12 tw-font-noto-sans  tw-mt-[42px] tw-mb-[12px] tw-border tw-z-40">
                                                <tr >
                                                    {headers.map(
                                                        (
                                                            {
                                                                label,
                                                                ...rest
                                                            }: {
                                                                label: string;
                                                                options: any;
                                                                map: string;
                                                            },
                                                            index: number
                                                        ) => {
                                                            return (
                                                                <th
                                                                    key={`${label}_${index}`}
                                                                    className="tw-sticky tw-text-left"
                                                                    scope="col"
                                                                    aria-sort={rest.options?.sort ? (sort.column === rest.map ? (sort.direction === "asc" ? "ascending" : "descending") : "none") : undefined}
                                                                >
                                                                    <div className="tw-flex tw-items-center tw-gap-1">
                                                                        {rest.options?.sort ? (
                                                                            /* A real button where a clickable div used to be —
                                                                               the testid predates the tag and stays verbatim. */
                                                                            <button
                                                                                type="button"
                                                                                data-testid={`list-listdetails-div-1-${index}`}
                                                                                className="tw-flex tw-items-center tw-gap-1 tw-text-title-header tw-text-ink-secondary"
                                                                                onClick={() => handleSortByColumn(rest.map)}
                                                                            >
                                                                                <span>{label}</span>
                                                                                {sort.column === rest.map
                                                                                    ? (sort.direction === "asc" ? <ArrowUp /> : <ArrowDown />)
                                                                                    : <Sort />}
                                                                            </button>
                                                                        ) : (
                                                                            <span
                                                                                data-testid={`list-listdetails-div-1-${index}`}
                                                                                className="tw-text-title-header tw-text-ink-secondary"
                                                                            >
                                                                                {label}
                                                                            </span>
                                                                        )}
                                                                        {rest.options?.infoMessage && (
                                                                            <Tooltip content={rest.options?.infoMessage} usePortal>
                                                                                <Button variant="subtle" size="sm" aria-label={`About ${label}`}>
                                                                                    <InfoCircle size={16} />
                                                                                </Button>
                                                                            </Tooltip>
                                                                        )}
                                                                    </div>
                                                                </th>
                                                            );
                                                        }
                                                    )}
                                                </tr>
                                            </thead>
                                            <tbody >
                                                {tableData?.map((item: Row, index) => {
                                                    const hasHelpText = Object.prototype.hasOwnProperty.call(item, "helpText")
                                                    return (
                                                        <Draggable
                                                            key={index}
                                                            draggableId={`${index}`}
                                                            index={index}
                                                            isDragDisabled={true}
                                                        >
                                                            {(provided) => (
                                                                <tr
                                                                    ref={provided.innerRef}
                                                                    {...provided.draggableProps}
                                                                    {...provided.dragHandleProps}
                                                                    className={`tw-w-full tw-h-12 tw-bg-surface  tw-border tw-border-edge`}
                                                                >
                                                                    <td
                                                                        className={`tw-h-12 ${index % 2 === 0
                                                                            ? "tw-bg-surface"
                                                                            : "tw-bg-surface-sunken"
                                                                            } tw-pl-4 tw-pr-4`}
                                                                    >
                                                                        <Drag_n_drop />
                                                                    </td>
                                                                    <td
                                                                        className={`tw-h-12 tw-w-10 tw-font-semibold tw-text-ink-secondary tw-select-none ${index % 2 === 0 ? "tw-bg-surface" : "tw-bg-surface-sunken"}`}
                                                                    >
                                                                        {index + 1}
                                                                    </td>
                                                                    <td
                                                                        className={`tw-h-12 tw-max-h-12 ${index % 2 === 0
                                                                            ? "tw-bg-surface"
                                                                            : "tw-bg-surface-sunken"
                                                                            } tw-cursor-pointer`}
                                                                    >
                                                                        {item?.image ? (
                                                                            <img
                                                                                src={item?.image}
                                                                                alt="image"
                                                                                className="tw-h-12 tw-w-12"
                                                                            />
                                                                        ) : (
                                                                            <ImageIcon size={24} className="tw-text-ink-tertiary" />
                                                                        )}
                                                                    </td>
                                                                    <td
                                                                        className={`tw-h-12 tw-w-[160px] tw-min-w-[160px] tw-max-w-[160px] ${index % 2 === 0 ? "tw-bg-surface" : "tw-bg-surface-sunken"}`}
                                                                    >
                                                                        <TagCell
                                                                            value={item.tag || ""}
                                                                            onChange={() => {}}
                                                                            allTags={allTags}
                                                                            disabled={true}
                                                                            data-testid={`list-tagcell-${item.id ?? index}`}
                                                                        />
                                                                    </td>
                                                                    <td
                                                                        className={`tw-h-12 ${index % 2 === 0
                                                                            ? "tw-bg-surface"
                                                                            : "tw-bg-surface-sunken"
                                                                            } tw-w-[30%]`}
                                                                    >
                                                                        <TextField
                                                                            label=""
                                                                            aria-label={`Title for row ${index + 1}`}
                                                                            placeholder="Enter Title"
                                                                            value={item.title ?? ""}
                                                                            disabled
                                                                            data-testid={`list-listdetails-title-${item.id ?? index}`}
                                                                            className="tw-w-[95%]"
                                                                        />
                                                                    </td>
                                                                    <td
                                                                        className={`tw-h-12 ${index % 2 === 0
                                                                            ? "tw-bg-surface"
                                                                            : "tw-bg-surface-sunken"
                                                                            } tw-w-[30%]`}
                                                                    >
                                                                        <TextField
                                                                            label=""
                                                                            aria-label={`Display value for row ${index + 1}`}
                                                                            placeholder="Enter Display Value"
                                                                            value={item.display_value ?? ""}
                                                                            disabled
                                                                            data-testid={`list-listdetails-display-value-${item.id ?? index}`}
                                                                            className="tw-w-[95%]"
                                                                        />
                                                                    </td>
                                                                    <td
                                                                        className={`tw-h-12 ${index % 2 === 0
                                                                            ? "tw-bg-surface"
                                                                            : "tw-bg-surface-sunken"
                                                                            } tw-w-[20]`}
                                                                    >
                                                                        <TextField
                                                                            label=""
                                                                            aria-label={`Math value for row ${index + 1}`}
                                                                            placeholder="Enter Math Value"
                                                                            type="number"
                                                                            value={item.math_value?.toString() ?? ""}
                                                                            disabled
                                                                            data-testid={`list-listdetails-math-value-${item.id ?? index}`}
                                                                            className="tw-w-[95%]"
                                                                        />
                                                                    </td>
                                                                    <td
                                                                        className={`tw-h-12 ${index % 2 === 0
                                                                            ? "tw-bg-surface"
                                                                            : "tw-bg-surface-sunken"
                                                                            }`}
                                                                    >
                                                                        <Toggle
                                                                            label=""
                                                                            aria-label={`Show or hide ${item.title || `row ${index + 1}`}`}
                                                                            checked={!!item?.visibility}
                                                                            disabled
                                                                            onChange={() =>
                                                                                onVisibilityChangeHandler(index)
                                                                            }
                                                                            data-testid={`list-listdetails-visibility-${item.id ?? index}`}
                                                                        />
                                                                    </td>
                                                                    <td className={`tw-h-12 ${index % 2 === 0 ? "tw-bg-surface" : "tw-bg-surface-sunken"}`}>
                                                                        {/* § 212 — the read-only twin of ListEdit's help-text button:
                                                                            still hardcoded disabled (the details page is view-only), but
                                                                            the disabled square now says so (cursor-not-allowed, was
                                                                            cursor-pointer lying on a disabled control). */}
                                                                        <IconButton data-testid={`list-listdetails-help-text-${item.id ?? index}`}
                                                                            aria-label={hasHelpText ? "View help text" : "Add help text"}
                                                                            disabled
                                                                        >
                                                                            {hasHelpText ?
                                                                                <InfoCircle size={16} /> :
                                                                                <Add />
                                                                            }
                                                                        </IconButton>
                                                                    </td>
                                                                    <td
                                                                        className={`tw-h-12 ${index % 2 === 0
                                                                            ? "tw-bg-surface"
                                                                            : "tw-bg-surface-sunken"
                                                                            }`}
                                                                    ></td>
                                                                </tr>
                                                            )}
                                                        </Draggable>
                                                    );
                                                })}

                                                {provided.placeholder}
                                            </tbody>
                                        </table>
                                    </div>

                                )}
                            </Droppable>
                        </DragDropContext>
                    </Card>
                    </div>
                </>
            )}
        </React.Fragment>
    );
};


export default ListsDetails;

